/**
 * The stat vocabulary the manual prediction tool offers, per sport.
 *
 * Free text was the old input, and it let an admin type anything — including
 * a category no resolver knows, which produces a prop we can sell and never
 * settle. These are the choices instead.
 *
 * The list is scoped by PROP TYPE, not just sport, because the settleable
 * stats genuinely differ by subject:
 *
 *   MANUAL_MATCH_THRESHOLD  → GAME_*   both teams' box scores summed
 *   MANUAL_TEAM_THRESHOLD   → TEAM_*   one side of the box score
 *   MANUAL_PLAYER_BOOLEAN   → player   one player's gamelog
 *
 * Match scope matters too. EACH_TEAM fan-out rewrites a match prop into two
 * MANUAL_TEAM_THRESHOLD props, one per side, so it needs TEAM_* categories —
 * a GAME_* category there resolves to the game-wide total for BOTH props,
 * making two differently-worded props settle identically.
 *
 * Every id here is checked against the real resolver by the spec beside this
 * file. An option that cannot settle fails CI rather than reaching an admin.
 */

/** How a prop written against this stat gets resolved. */
export type ManualStatSettlement = 'AUTO' | 'MANUAL';

export interface ManualStatOption {
  /** Persisted verbatim to UndercardProp.statCategory. */
  id: string;
  /** What the admin reads in the dropdown. */
  label: string;
  /**
   * How the stat reads inside the generated question — "Will X record 3+
   * ___?". Without this the id is de-underscored, which yields wording like
   * "3+ team longest field goal".
   */
  phrase: string;
  /** Starting line. A .5 line cannot push; a whole number can. */
  defaultThreshold: number;
  /** Step for the threshold input — yards move in bigger jumps than counts. */
  step: number;
  settlement: ManualStatSettlement;
  /** One line under the dropdown: what actually resolves this. */
  note?: string;
  /**
   * The same stat at the other scope, used when the admin flips match scope
   * so the selection follows instead of resetting to the top of the list.
   */
  counterpart?: string;
  /**
   * For game-wide stats, how the two sides combine — and therefore how the
   * question has to read.
   *
   * SUM adds both teams, so it is a MATCH TOTAL: "Will 4.5+ touchdowns be
   * recorded in X vs Y?". Wording a summed stat as "Will X or Y record 4.5+
   * touchdowns?" tells the user one side has to reach the line when the two
   * are actually added together — a prop that reads harder than it settles.
   *
   * MAX takes the better of the two, which genuinely IS an either-team
   * question: the longest field goal in the game clears 47.5 exactly when one
   * team's longest does.
   *
   * Absent on team- and player-scoped stats — those already name their subject.
   */
  aggregate?: 'SUM' | 'MAX';
  /**
   * For player stats: the odds-side prop type whose resolver already reads
   * this stat off PlayerGameStat. Pins each option to a real column and
   * makes wiring player auto-settlement a lookup rather than a re-mapping.
   */
  playerPropType?: NflPlayerPropTypeId;
  /**
   * Positions that can carry this stat — a QB market must not be pinned to a
   * receiver. Mirrors PROP_TYPE_POSITIONS in prop-description, restated here
   * so the catalog stays dependency-free; the spec asserts they agree.
   */
  positions?: readonly string[];
}

/** The offensive player universe. There are no defenders in the Player table. */
const SKILL_POSITIONS = ['QB', 'RB', 'WR', 'TE'] as const;

/**
 * Mirrors NflPlayerPropType in main-event.service. Declared locally rather
 * than imported so this module stays dependency-free and can be mirrored into
 * jest-client; the spec asserts the two stay in step.
 */
export type NflPlayerPropTypeId =
  | 'PLAYER_PASS_YARDS' | 'PLAYER_PASS_TDS' | 'PLAYER_PASS_COMPLETIONS'
  | 'PLAYER_PASS_INTERCEPTIONS' | 'PLAYER_RUSH_YARDS' | 'PLAYER_RUSH_ATTEMPTS'
  | 'PLAYER_RECEIVING_YARDS' | 'PLAYER_RECEPTIONS' | 'PLAYER_RUSH_RECEIVING_YARDS'
  | 'PLAYER_ANYTIME_TD' | 'PLAYER_KICKING_POINTS';

/**
 * The box-score categories, mirroring ManualTeamStatCategory in
 * services/stats/nfl-manual-prop-stats. Declared here rather than imported so
 * this module has no server-side dependency and can be mirrored into
 * jest-client verbatim; the spec asserts every member is one the resolver
 * knows, at compile time and again at runtime.
 */
export type NflTeamStatCategoryId =
  | 'GAME_TOTAL_TOUCHDOWNS' | 'GAME_TOTAL_FIELD_GOALS' | 'GAME_FIELD_GOALS_50_PLUS'
  | 'GAME_LONGEST_FIELD_GOAL' | 'GAME_TOTAL_PASSING_YARDS' | 'GAME_TOTAL_RUSHING_YARDS'
  | 'GAME_TOTAL_FIRST_DOWNS' | 'GAME_TOTAL_PENALTIES' | 'GAME_TOTAL_TURNOVERS'
  | 'GAME_TWO_POINT_CONVERSIONS'
  | 'TEAM_TOUCHDOWNS' | 'TEAM_FIELD_GOALS' | 'TEAM_LONGEST_FIELD_GOAL'
  | 'TEAM_PASSING_YARDS' | 'TEAM_RUSHING_YARDS' | 'TEAM_FIRST_DOWNS'
  | 'TEAM_OFFENSE_YARDS' | 'TEAM_PENALTY_YARDS' | 'TEAM_TURNOVERS';

type TeamScopedOption = ManualStatOption & { id: NflTeamStatCategoryId };

const AUTO = 'Settles from the team box score.';

/** Both teams combined. */
const NFL_GAME_STATS: TeamScopedOption[] = [
  { id: 'GAME_TOTAL_TOUCHDOWNS', label: 'Touchdowns', phrase: 'touchdowns', defaultThreshold: 4.5, step: 0.5, settlement: 'AUTO', note: AUTO, counterpart: 'TEAM_TOUCHDOWNS', aggregate: 'SUM' },
  { id: 'GAME_TOTAL_FIELD_GOALS', label: 'Field goals', phrase: 'field goals', defaultThreshold: 3.5, step: 0.5, settlement: 'AUTO', note: AUTO, counterpart: 'TEAM_FIELD_GOALS', aggregate: 'SUM' },
  { id: 'GAME_TOTAL_PASSING_YARDS', label: 'Passing yards (net)', phrase: 'passing yards', defaultThreshold: 450, step: 25, settlement: 'AUTO', note: 'Net of sacks, per the official box score.', counterpart: 'TEAM_PASSING_YARDS', aggregate: 'SUM' },
  { id: 'GAME_TOTAL_RUSHING_YARDS', label: 'Rushing yards', phrase: 'rushing yards', defaultThreshold: 220, step: 10, settlement: 'AUTO', note: AUTO, counterpart: 'TEAM_RUSHING_YARDS', aggregate: 'SUM' },
  { id: 'GAME_TOTAL_FIRST_DOWNS', label: 'First downs', phrase: 'first downs', defaultThreshold: 38.5, step: 0.5, settlement: 'AUTO', note: AUTO, counterpart: 'TEAM_FIRST_DOWNS', aggregate: 'SUM' },
  { id: 'GAME_TOTAL_TURNOVERS', label: 'Turnovers', phrase: 'turnovers', defaultThreshold: 2.5, step: 0.5, settlement: 'AUTO', note: AUTO, counterpart: 'TEAM_TURNOVERS', aggregate: 'SUM' },
  { id: 'GAME_TOTAL_PENALTIES', label: 'Penalties', phrase: 'penalties', defaultThreshold: 12.5, step: 0.5, settlement: 'AUTO', note: AUTO, aggregate: 'SUM' },
  { id: 'GAME_FIELD_GOALS_50_PLUS', label: 'Field goals of 50+ yards', phrase: 'field goals of 50+ yards', defaultThreshold: 0.5, step: 0.5, settlement: 'AUTO', note: AUTO, aggregate: 'SUM' },
  { id: 'GAME_LONGEST_FIELD_GOAL', label: 'Longest field goal', phrase: 'yards on the longest field goal', defaultThreshold: 47.5, step: 1, settlement: 'AUTO', note: 'Longest made kick by either team.', counterpart: 'TEAM_LONGEST_FIELD_GOAL', aggregate: 'MAX' },
  { id: 'GAME_TWO_POINT_CONVERSIONS', label: 'Two-point conversions', phrase: 'two-point conversions', defaultThreshold: 0.5, step: 0.5, settlement: 'AUTO', note: AUTO, aggregate: 'SUM' },
];

/** One named team's side of the box score. */
const NFL_TEAM_STATS: TeamScopedOption[] = [
  { id: 'TEAM_TOUCHDOWNS', label: 'Touchdowns', phrase: 'touchdowns', defaultThreshold: 2.5, step: 0.5, settlement: 'AUTO', note: AUTO, counterpart: 'GAME_TOTAL_TOUCHDOWNS' },
  { id: 'TEAM_FIELD_GOALS', label: 'Field goals', phrase: 'field goals', defaultThreshold: 1.5, step: 0.5, settlement: 'AUTO', note: AUTO, counterpart: 'GAME_TOTAL_FIELD_GOALS' },
  { id: 'TEAM_PASSING_YARDS', label: 'Passing yards (net)', phrase: 'passing yards', defaultThreshold: 225, step: 25, settlement: 'AUTO', note: 'Net of sacks, per the official box score.', counterpart: 'GAME_TOTAL_PASSING_YARDS' },
  { id: 'TEAM_RUSHING_YARDS', label: 'Rushing yards', phrase: 'rushing yards', defaultThreshold: 110, step: 10, settlement: 'AUTO', note: AUTO, counterpart: 'GAME_TOTAL_RUSHING_YARDS' },
  { id: 'TEAM_OFFENSE_YARDS', label: 'Total yards', phrase: 'total yards', defaultThreshold: 340, step: 10, settlement: 'AUTO', note: 'Passing plus rushing, net of sacks.' },
  { id: 'TEAM_FIRST_DOWNS', label: 'First downs', phrase: 'first downs', defaultThreshold: 19.5, step: 0.5, settlement: 'AUTO', note: AUTO, counterpart: 'GAME_TOTAL_FIRST_DOWNS' },
  { id: 'TEAM_TURNOVERS', label: 'Turnovers', phrase: 'turnovers', defaultThreshold: 1.5, step: 0.5, settlement: 'AUTO', note: 'Giveaways by this team.', counterpart: 'GAME_TOTAL_TURNOVERS' },
  { id: 'TEAM_PENALTY_YARDS', label: 'Penalty yards', phrase: 'penalty yards', defaultThreshold: 55, step: 5, settlement: 'AUTO', note: AUTO },
  { id: 'TEAM_LONGEST_FIELD_GOAL', label: 'Longest field goal', phrase: 'yards on their longest field goal', defaultThreshold: 42.5, step: 1, settlement: 'AUTO', note: 'Longest made kick by this team.', counterpart: 'GAME_LONGEST_FIELD_GOAL' },
];

/**
 * One player's gamelog, resolved through the prop type whose settlement
 * resolver already reads that column off PlayerGameStat.
 *
 * A player prop settles only against a STORED playerId — the modal picks a
 * real roster row rather than taking a typed name. Matching a free-text name
 * at settlement time would either guess or hold; binding it at creation time
 * does neither.
 *
 * Offense only. PlayerGameStat is offense-shaped and the NFL player universe
 * carries no defenders, so a defensive market (sacks) has no row that could
 * ever satisfy it — offering one would guarantee a prop we can sell and never
 * settle, which is the failure this catalog exists to prevent.
 */
const AUTO_PLAYER = 'Settles from the player box score.';

const NFL_PLAYER_STATS: ManualStatOption[] = [
  { id: 'PASSING_YARDS', label: 'Passing yards', phrase: 'passing yards', defaultThreshold: 250, step: 25, settlement: 'AUTO', note: AUTO_PLAYER, playerPropType: 'PLAYER_PASS_YARDS', positions: ['QB'] },
  { id: 'PASSING_TOUCHDOWNS', label: 'Passing touchdowns', phrase: 'passing touchdowns', defaultThreshold: 1.5, step: 0.5, settlement: 'AUTO', note: AUTO_PLAYER, playerPropType: 'PLAYER_PASS_TDS', positions: ['QB'] },
  { id: 'COMPLETIONS', label: 'Completions', phrase: 'completions', defaultThreshold: 22.5, step: 0.5, settlement: 'AUTO', note: AUTO_PLAYER, playerPropType: 'PLAYER_PASS_COMPLETIONS', positions: ['QB'] },
  { id: 'INTERCEPTIONS', label: 'Interceptions thrown', phrase: 'interceptions', defaultThreshold: 0.5, step: 0.5, settlement: 'AUTO', note: AUTO_PLAYER, playerPropType: 'PLAYER_PASS_INTERCEPTIONS', positions: ['QB'] },
  { id: 'RUSHING_YARDS', label: 'Rushing yards', phrase: 'rushing yards', defaultThreshold: 55, step: 5, settlement: 'AUTO', note: AUTO_PLAYER, playerPropType: 'PLAYER_RUSH_YARDS', positions: SKILL_POSITIONS },
  { id: 'RUSHING_ATTEMPTS', label: 'Rushing attempts', phrase: 'rushing attempts', defaultThreshold: 12.5, step: 0.5, settlement: 'AUTO', note: AUTO_PLAYER, playerPropType: 'PLAYER_RUSH_ATTEMPTS', positions: SKILL_POSITIONS },
  { id: 'RECEIVING_YARDS', label: 'Receiving yards', phrase: 'receiving yards', defaultThreshold: 55, step: 5, settlement: 'AUTO', note: AUTO_PLAYER, playerPropType: 'PLAYER_RECEIVING_YARDS', positions: SKILL_POSITIONS },
  { id: 'RECEPTIONS', label: 'Receptions', phrase: 'receptions', defaultThreshold: 4.5, step: 0.5, settlement: 'AUTO', note: AUTO_PLAYER, playerPropType: 'PLAYER_RECEPTIONS', positions: SKILL_POSITIONS },
  { id: 'RUSHING_RECEIVING_YARDS', label: 'Rushing + receiving yards', phrase: 'rushing + receiving yards', defaultThreshold: 75, step: 5, settlement: 'AUTO', note: AUTO_PLAYER, playerPropType: 'PLAYER_RUSH_RECEIVING_YARDS', positions: SKILL_POSITIONS },
  { id: 'TOUCHDOWNS', label: 'Touchdowns (rush + rec)', phrase: 'touchdowns', defaultThreshold: 0.5, step: 0.5, settlement: 'AUTO', note: 'Rushing and receiving only — a passer throwing a TD is not scoring.', playerPropType: 'PLAYER_ANYTIME_TD', positions: SKILL_POSITIONS },
  { id: 'KICKING_POINTS', label: 'Kicking points', phrase: 'kicking points', defaultThreshold: 7.5, step: 0.5, settlement: 'AUTO', note: '3 per field goal, 1 per extra point — sportsbook scoring, not PPR.', playerPropType: 'PLAYER_KICKING_POINTS', positions: ['K'] },
];

/**
 * The pre-NFL vocabulary, unchanged. Those sports have no stat ingest, so
 * their props are settled by hand whatever the category says.
 */
const LEGACY_STATS: ManualStatOption[] = [
  'GOALS', 'POINTS', 'ASSISTS', 'REBOUNDS', 'SHOTS', 'SAVES', 'STRIKEOUTS',
].map((id) => ({
  id,
  label: id.charAt(0) + id.slice(1).toLowerCase(),
  phrase: id.toLowerCase(),
  defaultThreshold: 1,
  step: 0.5,
  settlement: 'MANUAL' as const,
}));

const ALL_NFL_STATS = [...NFL_GAME_STATS, ...NFL_TEAM_STATS, ...NFL_PLAYER_STATS];

/** Mirrors MatchScope in manual-prop.template. */
export type ManualStatMatchScope = 'EITHER_TEAM' | 'EACH_TEAM' | 'COMBINED_TOTAL';

/**
 * Options for a sport, prop type and match scope. Empty when the prop type
 * takes no stat, so the caller can hide the field.
 */
export function manualStatOptions(
  sport: string | undefined,
  propType: string,
  matchScope: ManualStatMatchScope = 'EITHER_TEAM'
): ManualStatOption[] {
  if (propType === 'MANUAL_GENERIC' || propType === 'ODDS_BASED') return [];
  if (sport !== 'NFL') return LEGACY_STATS;

  switch (propType) {
    case 'MANUAL_MATCH_THRESHOLD':
      // EACH_TEAM fans out into per-team props — see the note at the top.
      return matchScope === 'EACH_TEAM' ? NFL_TEAM_STATS : NFL_GAME_STATS;
    case 'MANUAL_TEAM_THRESHOLD':
      return NFL_TEAM_STATS;
    case 'MANUAL_PLAYER_BOOLEAN':
      return NFL_PLAYER_STATS;
    default:
      return [];
  }
}

/**
 * Pick the selection to carry across a change of prop type or match scope:
 * keep it if the new list has it, follow the GAME_/TEAM_ counterpart if there
 * is one, else fall back to the head of the list.
 */
export function reconcileStatCategory(current: string, options: ManualStatOption[]): string {
  if (options.length === 0) return '';
  if (options.some((o) => o.id === current)) return current;

  const previous = ALL_NFL_STATS.find((o) => o.id === current);
  if (previous?.counterpart && options.some((o) => o.id === previous.counterpart)) {
    return previous.counterpart;
  }
  return options[0].id;
}

/**
 * The player prop type whose settlement resolver reads this stat, or null for
 * anything that is not a player stat. This is the seam player-prop settlement
 * resolves through, which is what keeps the dropdown and the resolver from
 * drifting: an option with no mapping cannot be offered.
 */
export function playerPropTypeForStat(
  statCategory: string | null | undefined
): NflPlayerPropTypeId | null {
  if (!statCategory) return null;
  return NFL_PLAYER_STATS.find((o) => o.id === statCategory)?.playerPropType ?? null;
}

/**
 * Positions that can record this stat, or null when it is not a player stat.
 *
 * The API validates a binding against this, so a receiver cannot be pinned to
 * a passing-yards market no matter what the client submits — settlement reads
 * one column, and a position-incompatible player never fills it.
 */
export function playerStatPositions(
  statCategory: string | null | undefined
): readonly string[] | null {
  if (!statCategory) return null;
  return NFL_PLAYER_STATS.find((o) => o.id === statCategory)?.positions ?? null;
}

/**
 * The single-team form of a stat, for fan-out that splits a match prop into
 * one prop per side. Returns the input unchanged when there is no team-scoped
 * equivalent, so an unknown or already-team-scoped category passes through.
 */
export function teamScopedStatCategory(statCategory: string | null | undefined): string | null {
  if (!statCategory) return statCategory ?? null;
  const option = ALL_NFL_STATS.find((o) => o.id === statCategory);
  if (!option?.counterpart?.startsWith('TEAM_')) return statCategory;
  return option.counterpart;
}

/**
 * Does this stat ADD both teams together?
 *
 * The question has to follow the answer. A summed category is a match total,
 * so it must be worded as one — "Will 4.5+ touchdowns be recorded in X vs Y?"
 * — and never as "Will X or Y record 4.5+ touchdowns?", which says a single
 * side has to reach the line and is a materially harder question than the one
 * that settles. Two teams at 3 TDs each settles that prop HIT while every
 * reader of the either-team wording would call it a miss.
 *
 * False for team-scoped stats (already one side's question), for a MAX like
 * the longest field goal (one side clearing the line IS the game's max), and
 * for anything outside the catalog, so hand-typed and legacy categories keep
 * whatever wording they had.
 */
export function combinesBothTeams(statCategory: string | null | undefined): boolean {
  if (!statCategory) return false;
  return ALL_NFL_STATS.find((o) => o.id === statCategory)?.aggregate === 'SUM';
}

/**
 * How a stat reads inside a generated question. Null for anything unknown,
 * so the caller keeps its existing de-underscoring fallback and a hand-typed
 * or legacy category is never rewritten.
 */
export function manualStatPhrase(statCategory: string | null | undefined): string | null {
  if (!statCategory) return null;
  return ALL_NFL_STATS.find((o) => o.id === statCategory)?.phrase ?? null;
}

/** Look up one option, for the threshold step and the hint line. */
export function findManualStatOption(
  sport: string | undefined,
  propType: string,
  matchScope: ManualStatMatchScope,
  id: string
): ManualStatOption | undefined {
  return manualStatOptions(sport, propType, matchScope).find((o) => o.id === id);
}

export const NFL_MANUAL_STATS = {
  game: NFL_GAME_STATS,
  team: NFL_TEAM_STATS,
  player: NFL_PLAYER_STATS,
};
